import { Box, Stack } from "@mui/material";
import { useEffect } from "react";

import { File } from "@/components/graphql/schema.graphql";
import { useImageLoader } from "@/hooks/useImageLoader";

import { CircularProgress, Typography } from "../../components/ui-v2";
import { GalleryImagePreviewLayers } from "./GalleryImagePreviewLayers";
import { GalleryImagePreviewSimple } from "./GalleryImagePreviewSimple";
import { GalleryImagePreviewSlider } from "./GalleryImagePreviewSlider";

type VisualizationType = "single" | "layers" | "compare";

interface GalleryImagePreviewProps {
  imageUrl: string;
  imageSrcSet: string;
  sourceUrl?: string;
  fileMetadata?: File["metadata"];
  attachmentUrls?: File["attachmentUrls"];
  onLoad: () => void;
}

export const GalleryImagePreview = (props: GalleryImagePreviewProps) => {
  const {
    imageUrl,
    imageSrcSet,
    sourceUrl,
    fileMetadata,
    attachmentUrls,
    onLoad,
  } = props;

  const { image: loadedImage, isLoading: isImageLoading } =
    useImageLoader(imageUrl);
  const { image: loadedSourceImage, isLoading: isSourceImageLoading } =
    useImageLoader(sourceUrl ?? "");
  const isLoading = isImageLoading || isSourceImageLoading;
  const shouldShowImages = !!loadedImage && (!sourceUrl || !!loadedSourceImage);

  let visualizationType: VisualizationType = "single";
  if (fileMetadata?.rasterLayers && fileMetadata.rasterLayers.length > 0) {
    visualizationType = "layers";
  } else if (sourceUrl) {
    visualizationType = "compare";
  }

  useEffect(() => {
    if (!isLoading) {
      onLoad();
    }
  }, [isLoading, onLoad]);

  const aspectRatio = `${loadedImage?.width || 1} / ${loadedImage?.height || 1}`;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!shouldShowImages && (
        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          {isLoading && <CircularProgress />}
          {!isLoading && (
            <Typography variant="large">Error loading image</Typography>
          )}
        </Stack>
      )}
      {shouldShowImages && visualizationType === "single" && (
        <GalleryImagePreviewSimple
          src={imageUrl}
          srcSet={imageSrcSet}
          aspectRatio={aspectRatio}
        />
      )}
      {shouldShowImages && visualizationType === "layers" && (
        <GalleryImagePreviewLayers
          fileMetadata={fileMetadata}
          attachmentUrls={attachmentUrls}
          aspectRatio={aspectRatio}
        />
      )}
      {shouldShowImages && visualizationType === "compare" && sourceUrl && (
        <GalleryImagePreviewSlider
          imageUrl={imageUrl}
          imageSrcSet={imageSrcSet}
          beforeUrl={sourceUrl}
          aspectRatio={aspectRatio}
        />
      )}
    </Box>
  );
};
